import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, ChevronRight, Trash2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useLanguage } from "@/context/LanguageContext";
import { dashboardLabels, supportLabels } from "@/i18n";
import { useNotifications } from "@/hooks/useNotifications";
import {
  getNotificationTemplate,
  resolveNotificationTitle,
} from "@/components/notifications/notificationTemplates";
import type { Notification } from "@/types/notification";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Compact dashboard strip: latest unread notifications.
 * Mobile max 2, desktop max 3. Hidden when empty.
 */
export function DashboardUnreadNotifications() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const labels = dashboardLabels(language)?.unreadNotifications;
  const support = supportLabels(language);
  const currentUserId = String(user?.id || "");
  const maxPreview = isMobile ? 2 : 3;

  const {
    notifications,
    isLoadingNotifications,
    deleteNotification,
    hasUnreadNotifications,
  } = useNotifications(10);

  const unreadPreview = useMemo(() => {
    return notifications.filter((n) => !n.read).slice(0, maxPreview);
  }, [notifications, maxPreview]);

  if (!user || isLoadingNotifications) return null;
  if (!hasUnreadNotifications || unreadPreview.length === 0) return null;

  const handleDelete = (e: React.MouseEvent, notificationId: number) => {
    e.preventDefault();
    e.stopPropagation();
    deleteNotification({ notificationId, userId: currentUserId });
    toast.success(support.notificationDeleted);
  };

  const titleFor = (n: Notification) => {
    try {
      const template = getNotificationTemplate(n.type, language);
      if (!template) return labels?.fallbackTitle ?? "Ny notis";
      return resolveNotificationTitle(
        template,
        (n.data || {}) as Record<string, unknown>,
      );
    } catch {
      return labels?.fallbackTitle ?? "Ny notis";
    }
  };

  return (
    <section
      className={cn(
        "mb-3 overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-br from-sky-500/[0.05] via-background to-background native-dashboard-banner",
        isNative || isMobile ? "px-3 py-2" : "px-3.5 py-3 sm:px-4 sm:py-3.5",
        isMobile ? "w-full" : "w-[42%] max-w-xl mx-auto",
      )}
      aria-label={labels?.title ?? "Olästa notiser"}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              "grid shrink-0 place-items-center rounded-md bg-sky-500/12 text-sky-700 dark:text-sky-300",
              isMobile ? "h-5 w-5" : "h-7 w-7 rounded-lg",
            )}
          >
            <Bell
              className={isMobile ? "h-2.5 w-2.5" : "h-3.5 w-3.5"}
              strokeWidth={2.25}
              aria-hidden
            />
          </span>
          <p
            className={cn(
              "truncate font-semibold tracking-tight text-foreground",
              isMobile ? "text-[11px]" : "text-[15px]",
            )}
          >
            {labels?.title ?? "Olästa notiser"}
          </p>
        </div>
        <Link
          to="/notifications"
          className={cn(
            "inline-flex shrink-0 items-center gap-0.5 font-medium text-muted-foreground transition-colors hover:text-foreground",
            isMobile ? "text-[10px]" : "text-sm",
          )}
        >
          {labels?.viewAll ?? "Visa alla"}
          <ChevronRight
            className={isMobile ? "h-3 w-3" : "h-4 w-4"}
            aria-hidden
          />
        </Link>
      </div>

      <ul className="divide-y divide-border/60">
        {unreadPreview.map((n) => (
          <li key={n.id}>
            <div className="flex items-center gap-1 py-1 first:pt-0 last:pb-0">
              <button
                type="button"
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-2 rounded-md text-left transition-colors hover:bg-muted/50",
                  isMobile ? "px-1 py-0.5" : "px-1.5 py-1.5",
                )}
                onClick={() => navigate("/notifications")}
              >
                <p
                  className={cn(
                    "min-w-0 flex-1 truncate font-medium text-foreground",
                    isMobile ? "text-[11px] leading-tight" : "text-sm",
                  )}
                >
                  {titleFor(n)}
                </p>
                <span
                  className={cn(
                    "shrink-0 tabular-nums text-muted-foreground",
                    isMobile ? "text-[9px]" : "text-xs",
                  )}
                >
                  {n.timestamp}
                </span>
              </button>
              <button
                type="button"
                className={cn(
                  "shrink-0 rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive",
                  isMobile ? "p-1.5" : "p-2",
                )}
                aria-label={labels?.deleteAria ?? "Radera notis"}
                onClick={(e) => handleDelete(e, n.id)}
              >
                <Trash2 className={isMobile ? "h-3 w-3" : "h-3.5 w-3.5"} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
